// Canonical types plus real-world variants found in the polygon specification CSV
// (FIS / BTS / PROA flavours of FTTH and the bare `VnV` for Wolf sub-projects).
// Kept on the same enum so DB CHECK constraints, DTO validation and frontend
// dropdowns stay in lock-step.
export const POLYGON_CONSTRUCTION_TYPES = [
  'FTTH',
  'FTTH FIS',
  'FTTH BTS',
  'FTTH PROA',
  'FTTH GF',
  'NGA',
  'ZPS',
  'FTK',
  'Wolf-VnV',
  'VnV',
] as const;
export type PolygonConstructionType = typeof POLYGON_CONSTRUCTION_TYPES[number];

/**
 * Polygon `constructionType === 'NGA'` (Specifikace → Typ výstavby / způsob stavby).
 * Akviziční souhlas: NGA → CETIN NGA šablona; jinak výchozí souhlas (optická přípojka).
 */
export function isPolygonNga(constructionType: string | null | undefined): boolean {
  return (constructionType ?? '').trim().toUpperCase() === 'NGA';
}

export const POLYGON_WORK_SCOPES = ['AKV', 'Leg', 'PD', 'Realizace', 'FTK', 'VR', 'DIO', 'OST'] as const;
export type PolygonWorkScope = typeof POLYGON_WORK_SCOPES[number];

export const POLYGON_WORK_CODES = ['OK', 'VR', 'DIO', 'AKV', 'PD', 'LEG', 'MAT', 'OST'] as const;
export type PolygonWorkCode = typeof POLYGON_WORK_CODES[number];

export const POLYGON_BUSINESS_STATUSES = ['Aktivní', 'Dofakturováno', 'Uzavřeno'] as const;
export type PolygonBusinessStatus = typeof POLYGON_BUSINESS_STATUSES[number];

/**
 * Stav stavebního plánu (`polygons.plan_status`; CHECK `chk_polygons_plan_status_allowed`).
 * Exactly the five Polygony portfolio values (1:1 with tabs).
 * Stored as Czech labels with diacritics.
 */
export const POLYGON_PLAN_STATUSES = [
  'nezařazené',
  'projekce',
  'příprava staveb',
  'stavba',
  'archiv',
] as const;
export type PolygonPlanStatus = typeof POLYGON_PLAN_STATUSES[number];

/** Default for new polygons (replaces legacy `aktivní`). */
export const POLYGON_PLAN_STATUS_DEFAULT: PolygonPlanStatus = 'stavba';

/**
 * Non-terminal lifetime — “Ukázat pouze aktivní” / API `planState=aktivni`.
 * Does not include `nezařazené` or terminal `archiv`.
 */
export const POLYGON_PLAN_STATUSES_IN_PROGRESS = [
  'projekce',
  'příprava staveb',
  'stavba',
] as const satisfies readonly PolygonPlanStatus[];

export const POLYGON_PLAN_STATUSES_TERMINAL = [
  'archiv',
] as const satisfies readonly PolygonPlanStatus[];

/**
 * ASCII slug keys for filters / CSS / Excel (no diacritics).
 * Empty/`neurceno` remains for NULL/blank plan_status.
 */
export type PolygonPlanStatusFilterSlug =
  | 'nezarazene'
  | 'projekce'
  | 'priprava_staveb'
  | 'stavba'
  | 'archiv';

export const POLYGON_PLAN_STATUS_TO_FILTER_SLUG: Record<PolygonPlanStatus, PolygonPlanStatusFilterSlug> = {
  'nezařazené': 'nezarazene',
  projekce: 'projekce',
  'příprava staveb': 'priprava_staveb',
  stavba: 'stavba',
  archiv: 'archiv',
};

export const POLYGON_PLAN_STATUS_FILTER_OPTIONS: ReadonlyArray<{
  value: PolygonPlanStatusFilterSlug;
  label: PolygonPlanStatus;
}> = POLYGON_PLAN_STATUSES.map((status) => ({
  value: POLYGON_PLAN_STATUS_TO_FILTER_SLUG[status],
  label: status,
}));

/**
 * Portfolio tabs (`/polygons` + list filters) — 1:1 with canonical `plan_status`.
 * Empty / unknown → `nezarazene`.
 */
export const POLYGON_PLAN_STATUS_TABS = [
  'nezarazene',
  'projekce',
  'priprava',
  'stavba',
  'archiv',
] as const;
export type PolygonPlanStatusTabId = typeof POLYGON_PLAN_STATUS_TABS[number];

export const POLYGON_PLAN_STATUS_TAB_LABELS: Record<PolygonPlanStatusTabId, string> = {
  nezarazene: 'Nezařazené',
  projekce: 'Projekce',
  priprava: 'Příprava',
  stavba: 'Stavba',
  archiv: 'Archiv',
};

export const POLYGON_PLAN_STATUS_TAB_STATUSES: Record<
  PolygonPlanStatusTabId,
  readonly PolygonPlanStatus[]
> = {
  nezarazene: ['nezařazené'],
  projekce: ['projekce'],
  priprava: ['příprava staveb'],
  stavba: ['stavba'],
  archiv: ['archiv'],
};

export const POLYGON_PLAN_STATUS_TAB_FILTER_OPTIONS: ReadonlyArray<{
  value: PolygonPlanStatusTabId;
  label: string;
}> = POLYGON_PLAN_STATUS_TABS.map((id) => ({
  value: id,
  label: POLYGON_PLAN_STATUS_TAB_LABELS[id],
}));

export function isPolygonPlanStatusTabId(
  value: string | null | undefined,
): value is PolygonPlanStatusTabId {
  return (POLYGON_PLAN_STATUS_TABS as readonly string[]).includes((value ?? '').trim());
}

/** Which portfolio tab a raw plan-status / projectState belongs to. */
export function polygonPlanStatusTabId(
  value: string | null | undefined,
): PolygonPlanStatusTabId {
  const normalized = normalizePolygonPlanStatus(value);
  if (!normalized || normalized === 'nezařazené') return 'nezarazene';
  for (const tabId of POLYGON_PLAN_STATUS_TABS) {
    if ((POLYGON_PLAN_STATUS_TAB_STATUSES[tabId] as readonly string[]).includes(normalized)) {
      return tabId;
    }
  }
  return 'nezarazene';
}

/** Empty `tabs` = no restriction (all statuses). */
export function polygonMatchesPlanStatusTabs(
  planStatus: string | null | undefined,
  tabs: readonly string[],
): boolean {
  if (!tabs.length) return true;
  return tabs.includes(polygonPlanStatusTabId(planStatus));
}

/**
 * Legacy / removed mezistavy → canonical portfolio status (migration + write-time normalize).
 *
 * Mapping (fail-closed):
 * - nepřiřazeno → nezařazené
 * - břemena → projekce
 * - inženýring / příprava zahájení → příprava staveb
 * - reklamace / aktivní → stavba
 * - uzavřená / zrušená / storno → archiv
 */
const POLYGON_PLAN_STATUS_LEGACY_ALIASES: Record<string, PolygonPlanStatus> = {
  // nezařazené
  'nepřiřazeno': 'nezařazené',
  neprirazeno: 'nezařazené',
  nezarazene: 'nezařazené',
  // projekce (+ removed břemena)
  břemena: 'projekce',
  bremena: 'projekce',
  // příprava staveb (+ removed inženýring / příprava zahájení)
  'inženýring': 'příprava staveb',
  inzenyring: 'příprava staveb',
  'příprava zahájení': 'příprava staveb',
  'priprava zahajeni': 'příprava staveb',
  priprava_zahajeni: 'příprava staveb',
  'priprava staveb': 'příprava staveb',
  priprava_staveb: 'příprava staveb',
  priprava: 'příprava staveb',
  // stavba (+ removed reklamace + legacy aktivní)
  aktivní: 'stavba',
  aktivni: 'stavba',
  active: 'stavba',
  reklamace: 'stavba',
  // archiv (+ removed uzavřená / zrušená)
  'uzavřená': 'archiv',
  'uzavřené': 'archiv',
  uzavřeno: 'archiv',
  uzavrena: 'archiv',
  uzavrene: 'archiv',
  uzavreno: 'archiv',
  closed: 'archiv',
  'zrušená': 'archiv',
  zrusena: 'archiv',
  zrušeno: 'archiv',
  cancelled: 'archiv',
  canceled: 'archiv',
  storno: 'archiv',
};

/**
 * Normalize a raw plan-status / projectState string to a canonical `POLYGON_PLAN_STATUSES` value.
 * Returns null for empty input; unknown non-empty values return null (caller may reject).
 */
export function normalizePolygonPlanStatus(value: string | null | undefined): PolygonPlanStatus | null {
  const text = (value ?? '').trim();
  if (!text) return null;
  const exact = (POLYGON_PLAN_STATUSES as readonly string[]).includes(text)
    ? (text as PolygonPlanStatus)
    : null;
  if (exact) return exact;
  const lowered = text.toLocaleLowerCase('cs-CZ');
  for (const status of POLYGON_PLAN_STATUSES) {
    if (status.toLocaleLowerCase('cs-CZ') === lowered) return status;
  }
  return POLYGON_PLAN_STATUS_LEGACY_ALIASES[lowered] ?? null;
}

export function isPolygonPlanStatusInProgress(value: string | null | undefined): boolean {
  const normalized = normalizePolygonPlanStatus(value);
  if (!normalized) return false;
  return (POLYGON_PLAN_STATUSES_IN_PROGRESS as readonly string[]).includes(normalized);
}

export function isPolygonPlanStatusTerminal(value: string | null | undefined): boolean {
  const normalized = normalizePolygonPlanStatus(value);
  if (!normalized) return false;
  return (POLYGON_PLAN_STATUSES_TERMINAL as readonly string[]).includes(normalized);
}

/**
 * Stavební deník se generuje / zakládá jen pro polygon ve stavu plánu `stavba`.
 * Ostatní stavy (projekce, příprava staveb, archiv, …) nové deníky nedostanou;
 * existující záznamy se nemažou.
 */
export function isPolygonEligibleForConstructionDiary(
  planStatus: string | null | undefined,
): boolean {
  return normalizePolygonPlanStatus(planStatus) === 'stavba';
}

/**
 * Values matched by SQL `lower(btrim(...)) IN (...)` for the in-progress portfolio filter.
 * Includes ASCII/diacritic aliases and removed mezistavy until data is fully migrated.
 */
export const POLYGON_PLAN_STATUS_IN_PROGRESS_SQL_VALUES: readonly string[] = [
  'projekce',
  'příprava staveb',
  'priprava staveb',
  'stavba',
  // removed mezistavy → in-progress targets
  'inženýring',
  'inzenyring',
  'příprava zahájení',
  'priprava zahajeni',
  'břemena',
  'bremena',
  'reklamace',
  // legacy aktivní → stavba
  'aktivní',
  'aktivni',
  'active',
];

/**
 * Manager-dashboard portfolio scope — only `plan_status` / projectState = `stavba`
 * (plus legacy aliases that normalize to stavba).
 */
export const POLYGON_PLAN_STATUS_STAVBA_SQL_VALUES: readonly string[] = [
  'stavba',
  // legacy / removed → stavba
  'aktivní',
  'aktivni',
  'active',
  'reklamace',
];

/** Closed-folder filter (`planState=uzavrene`) — canonical archiv + spelling variants. */
export const POLYGON_PLAN_STATUS_CLOSED_SQL_VALUES: readonly string[] = [
  'archiv',
  // removed / legacy closed
  'uzavřená',
  'uzavřené',
  'uzavřeno',
  'uzavrena',
  'uzavrene',
  'uzavreno',
  'closed',
  'zrušená',
  'zrusena',
  'zrušeno',
  'cancelled',
  'canceled',
  'storno',
];

/** Quoted list for embedding in SQL `IN (...)` (values are constants — no user input). */
export function polygonPlanStatusSqlInList(values: readonly string[]): string {
  return values.map((v) => `'${v.replace(/'/g, "''")}'`).join(', ');
}

export const POLYGON_PLAN_STATUS_IN_PROGRESS_SQL_IN = polygonPlanStatusSqlInList(
  POLYGON_PLAN_STATUS_IN_PROGRESS_SQL_VALUES,
);

export const POLYGON_PLAN_STATUS_STAVBA_SQL_IN = polygonPlanStatusSqlInList(
  POLYGON_PLAN_STATUS_STAVBA_SQL_VALUES,
);

export const POLYGON_PLAN_STATUS_CLOSED_SQL_IN = polygonPlanStatusSqlInList(
  POLYGON_PLAN_STATUS_CLOSED_SQL_VALUES,
);

export const POLYGON_BILLING_TYPES = ['Dílčí', 'Konečná'] as const;
export type PolygonBillingType = typeof POLYGON_BILLING_TYPES[number];

/** Zodpovědná pobočka (DB sloupec `polygons.responsible_branch`; validace DTO + CHECK). */
export const POLYGON_RESPONSIBLE_BRANCHES = [
  'ProTelco Lovosice',
  'ProTelco Ostrava',
  'ProTelco Pardubice',
  'Fiber Beta',
  'EXTERMIS',
] as const;
export type PolygonResponsibleBranch = typeof POLYGON_RESPONSIBLE_BRANCHES[number];

/** @deprecated Legacy etapizace (`metadata.constructionPhasePlan`) — Gantt auto-plánuje bez ručního plánu. */
export const CONSTRUCTION_PHASE_CATEGORIES = [
  'VYKOPY_POKLADKA',
  'POVRCHY',
  'ZAFUK_MT',
  'ZAFUK_OK',
  'MONTAZ_OPTIKY',
  'OTHER',
] as const;
export type ConstructionPhaseCategory = typeof CONSTRUCTION_PHASE_CATEGORIES[number];

export const CONSTRUCTION_PHASE_CATEGORY_LABELS: Record<ConstructionPhaseCategory, string> = {
  VYKOPY_POKLADKA: 'Výkopy a pokládka',
  POVRCHY: 'Povrchy',
  ZAFUK_MT: 'Záfuk MT',
  ZAFUK_OK: 'Záfuk OK',
  MONTAZ_OPTIKY: 'Montáž optiky',
  OTHER: 'Jiné',
};

/** Legacy: maximální počet položek v `constructionPhasePlan.phases` (jedna implicitní část polygonu). */
export const MAX_CONSTRUCTION_PHASES = 30;

/** Nový model: části polygonu (`constructionPhasePlan.stages`). */
export const MAX_CONSTRUCTION_STAGES = 25;
/** Maximální počet fází v jedné části polygonu. */
export const MAX_CONSTRUCTION_PHASES_PER_STAGE = 30;
/** Horní mez počtu fází přes všechny části (ochrana API). */
export const MAX_CONSTRUCTION_PHASES_TOTAL = 120;

export function isConstructionPhaseCategory(value: string): value is ConstructionPhaseCategory {
  return (CONSTRUCTION_PHASE_CATEGORIES as readonly string[]).includes(value);
}
